import * as React from "react";
import { VisuallyHidden } from "react-aria";
import { Flex } from "../flex";
import { ActionStatus } from "../../utils/types";
import {
  HiOutlineCheckCircle,
  HiOutlineDocumentAdd,
  HiOutlineExclamationCircle,
  HiOutlinePaperClip,
  HiOutlineRefresh,
  HiOutlineTrash,
} from "react-icons/hi";
import { Text } from "../text";
import { Loader } from "../loader";
import { IconButton } from "../button";
import { InputError } from "../input-error";
import { InputLabel } from "../input-label";
import { useDrop, FileDropItem } from "react-aria";
import { isEmptyArray } from "../../utils/assertions";
import { id } from "../../utils/helpers";
import { Color } from "../../utils/theme";

export type Upload = {
  name: string;
  url: string;
  id: string;
  file?: File;
  status?: ActionStatus;
  progress?: number;
};

// onChange
// 1. convert files into upload type with raw file
// onFileUpload should now take an Array<Upload> argument
// if you need to do a remote upload outside the component you have access to the raw files.
// write utility function to extract files from Upload object
// if you want to use the component as a controlled component, simply pass in an uploads prop.
// if it's a single upload, display inside component
// if it's multiple uploads show them in the list of uploads as file name and progress bar

interface FileUploaderProps {
  // add an async function that can be used to upload files in uncontrolled mode and track upload status instead of using the controlled component approach.
  onFileUpload?: (files: Array<Upload>) => void;
  onRemoveFile?: () => void;
  onStatusChange?: (status: ActionStatus) => void;
  error?: React.ReactNode;
  label?: React.ReactNode;
  allowMultiple?: boolean;
  uploads?: Array<Upload>;
  hideFileList?: boolean;
  color?: Color;
}

export const FileUploader = React.forwardRef<HTMLDivElement, FileUploaderProps>(
  (
    {
      onFileUpload,
      onRemoveFile,
      error,
      label,
      allowMultiple,
      uploads: controlledUploads,
      hideFileList,
      color = "gray",
    },
    ref
  ) => {
    const [_uploads, setUploads] = React.useState<Array<Upload>>([]);

    const uploads = controlledUploads ?? _uploads;

    const inputRef = React.useRef<HTMLInputElement>(null);
    const wrapperRef = React.useRef(null);

    const { dropProps, isDropTarget } = useDrop({
      ref: wrapperRef,
      async onDrop(e) {
        const files = await Promise.all(
          (
            e.items.filter(
              (item) => item.kind === "file"
            ) as Array<FileDropItem>
          ).map(async (file) => {
            const f = await file.getFile();
            return f;
          })
        );

        const uploads = files.map(convertFileToUpload);

        onUpload(uploads);
      },
    });

    function onUpload(uploads: Array<Upload>) {
      if (isEmptyArray(uploads)) {
        return;
      }

      setUploads(uploads);
      onFileUpload?.(uploads);
    }

    function onChange(e: React.ChangeEvent<HTMLInputElement>) {
      const rawFiles = e.target.files;

      if (rawFiles?.length) {
        const files = Array.from(rawFiles).map(convertFileToUpload);
        onUpload(files);
        inputRef.current && (inputRef.current.value = "");
      }
    }

    function convertFileToUpload(file: File): Upload {
      return {
        name: file.name,
        id: id(),
        url: URL.createObjectURL(file),
        status: "success",
        file,
      };
    }

    function handleRemoveFile() {
      setUploads([]);
      onRemoveFile?.();
    }

    function handleRetry() {
      console.log("retry");
    }

    const isIdle =
      allowMultiple ||
      (!allowMultiple && uploads[0]?.status === "idle") ||
      !uploads.length;
    const isUploading = !allowMultiple && uploads[0]?.status === "loading";
    const isUploaded = !allowMultiple && uploads[0]?.status === "success";
    const isFailed = !allowMultiple && uploads[0]?.status === "error";

    return (
      <Flex ref={ref} stack gap={"xs"}>
        {label && <InputLabel>{label}</InputLabel>}
        <label
          data-radius="lg"
          className="p-file-uploader"
          ref={wrapperRef}
          {...dropProps}
          data-color={color}
          data-dropping={isDropTarget}
          data-state={
            allowMultiple || !uploads.length ? "idle" : uploads[0]?.status
          }
        >
          <VisuallyHidden>
            <input
              ref={inputRef}
              multiple={allowMultiple}
              onChange={onChange}
              type={isIdle ? "file" : "hidden"}
            />
          </VisuallyHidden>

          {isIdle && (
            <Flex align="center" gap={"xs"}>
              <HiOutlineDocumentAdd className="state-icon" size={20} />
              <Text color={"inherit"} size="sm">
                Click or drag files here to upload
              </Text>
            </Flex>
          )}

          {isUploading && (
            <Flex align={"center"} gap={"xs"}>
              <Loader size="xs" />
              <Text color="inherit" size="sm">
                Name_of_file.ext / Uploading...
              </Text>
            </Flex>
          )}

          {isUploaded && (
            <Flex stretchX align={"center"} justify={"space-between"}>
              <Flex align={"center"} gap={"xs"}>
                <HiOutlineCheckCircle size={16} />
                <Text color="green" size="sm">
                  File successfully uploaded
                </Text>
              </Flex>

              <IconButton
                onPress={handleRemoveFile}
                variant={"outline"}
                color="green"
                size="xs"
              >
                <HiOutlineTrash size={16} />
              </IconButton>
            </Flex>
          )}

          {isFailed && (
            <Flex stretchX justify={"space-between"}>
              <Flex align={"center"} gap={"xs"}>
                <HiOutlineExclamationCircle className="state-icon" />
                <Text color={"tomato"} size="sm">
                  Failed to upload file
                </Text>
              </Flex>

              <IconButton
                onPress={handleRetry}
                variant={"outline"}
                color={"tomato"}
                size={"xs"}
              >
                <HiOutlineRefresh size={16} />
              </IconButton>
            </Flex>
          )}

          <Flex></Flex>
        </label>

        {uploads.length > 1 && !hideFileList && (
          <Flex
            style={{ marginTop: 10 }}
            stack
            gap={"sm"}
            className="upload-list"
          >
            {uploads.map((upload) => (
              <Flex align={"center"} gap={"xs"} key={upload.id}>
                {upload.status === "loading" && <Loader></Loader>}
                {upload.status === "success" && <HiOutlinePaperClip />}
                <Text size="sm" color={"gray"}>
                  {upload.name}
                </Text>
              </Flex>
            ))}
          </Flex>
        )}

        {error && <InputError>{error}</InputError>}
      </Flex>
    );
  }
);

FileUploader.displayName = "FileUploader";
